import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * @typedef {'natural' | 'pinned' | 'hidden'} AnnouncementPin
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Announcement show/hide on scroll (navbar stays sticky always).
   * natural = in document flow at top; pinned = fixed visible on scroll up; hidden = fixed off-screen.
   * @type {AnnouncementPin}
   */
  #announcementPin = 'natural';

  /**
   * Last measured announcement section height (layout), independent of pin visibility.
   * @type {number}
   */
  #announcementNaturalHeight = 0;

  /**
   * Flow spacer so position:fixed pin does not collapse document height / jump scrollTop.
   * @type {HTMLElement | null}
   */
  #announcementSpacer = null;

  /**
   * Cached announcement section (avoid querySelector on every scroll frame).
   * @type {HTMLElement | null}
   */
  #announcementSection = null;

  /**
   * Cached prefers-reduced-motion (avoid matchMedia on every pin flip).
   * @type {MediaQueryList | null}
   */
  #reduceMotionQuery = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  /** @returns {HTMLElement | null} */
  #getAnnouncementSection = () => {
    if (this.#announcementSection?.isConnected) return this.#announcementSection;

    const el = document.querySelector('#header-group > .announcement-bar-section');
    this.#announcementSection = el instanceof HTMLElement ? el : null;
    return this.#announcementSection;
  };

  /**
   * Measure announcement content height via scrollHeight (stable while the section is fixed).
   * @returns {number}
   */
  #measureAnnouncementNaturalHeight = () => {
    const el = this.#getAnnouncementSection();
    if (!el) return 0;

    const bar = el.querySelector('.announcement-bar');
    const candidate =
      bar instanceof HTMLElement && bar.scrollHeight > 0 ? bar.scrollHeight : el.scrollHeight;

    if (candidate > 0 && candidate !== this.#announcementNaturalHeight) {
      this.#announcementNaturalHeight = candidate;
      document.body.style.setProperty(
        '--announcement-bar-natural-height',
        `${Math.round(candidate)}px`
      );
    }

    return this.#announcementNaturalHeight;
  };

  /** @param {HTMLElement} announcement */
  #ensureAnnouncementSpacer = (announcement) => {
    if (this.#announcementSpacer?.isConnected) return this.#announcementSpacer;

    const spacer = document.createElement('div');
    spacer.className = 'announcement-bar-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    announcement.insertAdjacentElement('afterend', spacer);
    this.#announcementSpacer = spacer;
    return spacer;
  };

  /**
   * @param {AnnouncementPin} mode
   */
  #setAnnouncementPin = (mode) => {
    const el = this.#getAnnouncementSection();
    if (!el) {
      this.#announcementPin = 'natural';
      if (this.#announcementSpacer) this.#announcementSpacer.style.height = '0px';
      return;
    }

    const natural =
      this.#announcementNaturalHeight > 0
        ? this.#announcementNaturalHeight
        : this.#measureAnnouncementNaturalHeight();

    const spacer = this.#ensureAnnouncementSpacer(el);
    const nextSpacerHeight = mode === 'natural' ? '0px' : `${Math.round(natural)}px`;

    if (
      mode === this.#announcementPin &&
      el.dataset.announcementPin === mode &&
      spacer.style.height === nextSpacerHeight
    ) {
      return;
    }

    const prev = this.#announcementPin;
    // Animate only pinned ↔ hidden. Handoffs with `natural` snap (no flash from top).
    const animate =
      (prev === 'pinned' || prev === 'hidden') && (mode === 'pinned' || mode === 'hidden');
    this.#reduceMotionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
    const reduceMotion = this.#reduceMotionQuery.matches;

    if (!animate || reduceMotion) {
      document.body.classList.add('announcement-pin--snap');
    }

    this.#announcementPin = mode;
    el.dataset.announcementPin = mode;

    if (spacer.style.height !== nextSpacerHeight) {
      spacer.style.height = nextSpacerHeight;
    }

    if (!animate || reduceMotion) {
      requestAnimationFrame(() => {
        document.body.classList.remove('announcement-pin--snap');
      });
    }
  };

  /**
   * Prestige-style pin: nav stays always-sticky; only the sale bar hides/shows.
   * Skipped for sticky=scroll-up (Horizon already shows/hides the whole header).
   * @param {number} scrollTop
   * @param {boolean} isScrollingUp
   */
  #updateAnnouncementPin = (scrollTop, isScrollingUp) => {
    if (this.getAttribute('sticky') !== 'always') return;

    const el = this.#getAnnouncementSection();
    if (!el) {
      this.#setAnnouncementPin('natural');
      return;
    }

    const natural =
      this.#announcementNaturalHeight > 0
        ? this.#announcementNaturalHeight
        : this.#measureAnnouncementNaturalHeight();

    if (natural <= 0) {
      this.#setAnnouncementPin('natural');
      return;
    }

    if (scrollTop <= natural) {
      this.#setAnnouncementPin('natural');
      return;
    }

    this.#setAnnouncementPin(isScrollingUp ? 'pinned' : 'hidden');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    this.#updateAnnouncementPin(scrollTop, isScrollingUp);

    if (!this.#offscreen && stickyMode !== 'always') {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (scrollTop <= 1) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    const isAtTop = this.getBoundingClientRect().top >= 0;

    // Stock Horizon scroll-up header (navbar itself hides/shows)
    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Re-measure announcement after schedule/resize.
   * Only updates natural height — does NOT re-apply pin (avoids RO → pin → spacer → RO loops).
   */
  syncAnnouncementHeight = () => {
    this.#announcementSection = null;
    this.#measureAnnouncementNaturalHeight();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    // Sale-bar pin only with always-sticky nav (see #updateAnnouncementPin).
    if (stickyMode === 'always') {
      this.#measureAnnouncementNaturalHeight();
      this.#setAnnouncementPin('natural');
      this.#updateAnnouncementPin(this.#lastScrollTop, false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#announcementSpacer?.remove();
    this.#announcementSpacer = null;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  if (headerGroup) {
    const syncHeaderGroupHeights = () => {
      let headerGroupHeight = 0;
      const children = headerGroup.children;

      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (!(element instanceof HTMLElement)) continue;
        if (element.classList.contains('announcement-bar-spacer')) continue;
        headerGroupHeight += element.offsetHeight;
      }

      // Transparent header special case — keep in sync with theme.liquid / utilities.js
      if (
        header instanceof HTMLElement &&
        header.hasAttribute('transparent') &&
        header.parentElement?.nextElementSibling
      ) {
        headerGroupHeight += header.offsetHeight;
      }

      document.body.style.setProperty('--header-group-height', `${Math.round(headerGroupHeight)}px`);

      if (header && typeof header.syncAnnouncementHeight === 'function') {
        header.syncAnnouncementHeight();
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      syncHeaderGroupHeights();
    });

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    syncHeaderGroupHeights();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          // Spacer is our own node — observing it only creates resize feedback.
          if (node.classList.contains('announcement-bar-spacer')) continue;
          resizeObserver.observe(node);
        }

        syncHeaderGroupHeights();
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
